import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import ListFilters from '@/common/components/ListFilters'
import ListHeader from '@/common/components/ListHeader'
import Table from '@/common/components/Table'
import TextField from '@/common/components/TextField'
import useDebounce from '@/common/hooks/useDebounce'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { Mapper } from '@/common/services/mapper'
import { CompanyBranchData } from '@/common/types/companyBranches.type'
import { cn } from '@/common/utils/cn'
import { useAvailableCompanyBranches } from '@/features/companyBranches/hooks/useAvailableCompanyBranches'
import { defaultColumns } from '@/features/maintenanceSolicitations/constants/columns.constant'
import { solicitationKindOptions } from '@/features/maintenanceSolicitations/constants/solicitationKind.constant'
import { solicitationStatusOptions } from '@/features/maintenanceSolicitations/constants/solicitationStatus.constant'
import { useMaintenanceSolicitations } from '@/features/maintenanceSolicitations/hooks/useMaintenanceSolicitations'
import { MaintenanceSolicitationsTransformer } from '@/features/maintenanceSolicitations/services/maintenanceSolicitationsTransformer'
import {
  MaintenanceSolicitationData,
  MaintenanceSolicitationTableItem,
} from '@/features/maintenanceSolicitations/types/maintenanceSolicitation'
import { SearchIcon, SirenIcon } from 'lucide-react'

const List = () => {
  const { filterParams, setFilterParams } = useFilterParams()

  const [search, setSearch] = useDebounce(filterParams.search || '')
  const [searchClientBranch, setSearchClientBranch] = useDebounce('')

  const { data: clientBranchesOptions } = useAvailableCompanyBranches({
    filters: { search: searchClientBranch },
    mapper: Mapper.mapToOptions<CompanyBranchData>({ valueFieldName: 'id', labelFieldName: 'display_name' }),
  })

  const {
    data: maintenanceSolicitations,
    meta,
    isPending: maintenanceSolicitationsIsPending,
    error: maintenanceSolicitationsError = {} as any,
    refetch: refetchMaintenanceSolicitations,
  } = useMaintenanceSolicitations({
    filters: {
      search,
      status: filterParams.status?.value,
      clientBranchId: filterParams.clientBranch?.value,
      kind: filterParams.kind?.value,
      page: filterParams.page || 1,
    },
    mapper: Mapper.mapWithTransformer<MaintenanceSolicitationData[], MaintenanceSolicitationTableItem[]>({
      transformer: MaintenanceSolicitationsTransformer.tableTransformer(),
    }),
  })

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="Solicitações de manutenção"
        Icon={<SirenIcon className="size-10" strokeWidth={1} />}
        createButtonText="Nova Solicitação"
        dataTestid="create_maintenance_solicitation_button"
        operationKlassToCheckPermission="Client::Maintenance::Solicitations::Create"
        disableCreateButton={maintenanceSolicitationsIsPending || maintenanceSolicitationsError?.status === 403}
      />

      <ErrorMessageWrapper
        className="h-[calc(100vh-128px)]"
        statusCode={maintenanceSolicitationsError?.status}
        refetchFunction={refetchMaintenanceSolicitations}
      >
        <div className="h-[calc(100vh-128px)] flex flex-col gap-2">
          <div className="flex flex-col gap-4">
            <TextField
              name="search"
              LeftIcon={<SearchIcon />}
              placeholder="Pesquisar solicitações por Número de protocolo ou Equipamento"
              className="rounded-sm shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
              onChange={(e) => {
                setSearch((e.target as HTMLInputElement).value)
                setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }))
              }}
              value={filterParams.search}
            />
          </div>

          <ListFilters
            filters={[
              {
                className: '',
                onChange: (status) => setFilterParams((prev) => ({ ...prev, status })),
                value: filterParams.status,
                options: [{ label: 'Todos', value: '' }, ...solicitationStatusOptions],
                placeholder: 'Status',
              },
              {
                className: '',
                onChange: (clientBranch) => setFilterParams((prev) => ({ ...prev, clientBranch })),
                onInputChange: setSearchClientBranch,
                value: filterParams.clientBranch || '',
                options: [{ label: 'Todas', value: '' }, ...clientBranchesOptions],
                placeholder: 'Filial',
              },
              {
                className: '',
                onChange: (kind) => setFilterParams((prev) => ({ ...prev, kind })),
                value: filterParams.kind,
                options: [{ label: 'Todos', value: '' }, ...solicitationKindOptions],
                placeholder: 'Tipo de solicitação',
              },
            ]}
          />

          <Table
            className={cn('max-h-screen', {
              'shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]': !!maintenanceSolicitations?.length,
            })}
            columns={defaultColumns}
            isLoadingData={maintenanceSolicitationsIsPending}
            page={filterParams.page || 1}
            rows={maintenanceSolicitations || []}
            totalPages={meta.total_pages}
            totalCount={meta.total}
            hasPagination
            rowEmptyValue="--"
            setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  )
}

export default List
